/**
 * Course sequence generation using lookup table approach
 */

import { 
  generateSequenceFromCompleteLookup,
  getAvailableFinishTypes,
  getAvailableGateCombinations,
  validateAndAdjustConfig
} from './courseSequenceCompleteLookup.js';
import { COURSE_TYPES } from './constants.js';

export const generateSequenceString = (courseData) => {
  if (!courseData || !courseData.courseType) {
    return 'INVALID';
  }

  const {
    courseType,
    finishType = 'Start finish together',
    offsetEnabled = false,
    gates = [],
    windwardBeats = 2,
  } = courseData;

  // Map course type values back to their constant keys for lookup
  // This handles cases where courseType contains the display value instead of the key
  const courseTypeKey = courseType === 'Leeward' ? COURSE_TYPES.T_LEEWARD :
                       courseType === 'Windward' ? COURSE_TYPES.T_WINDWARD :
                       courseType === 'Inner Trapezoid' ? COURSE_TYPES.T_INNER_TRAPEZOID :
                       courseType === 'Outer Trapezoid' ? COURSE_TYPES.T_OUTER_TRAPEZOID :
                       courseType === 'Triangle' ? COURSE_TYPES.T_TRIANGLE :
                       null;
  if (!courseTypeKey) {
    return 'INVALID';
  }

  const gateConfig = {
    gate1: gates.includes('1') || gates.includes(1),
    gate3: gates.includes('3') || gates.includes(3), 
    gate4: gates.includes('4') || gates.includes(4),
    gate5: gates.includes('5') || gates.includes(5)
  };

  // Check for invalid combination: gate1 enabled AND offset enabled
  if (gateConfig.gate1 && offsetEnabled) {
    return 'INVALID';
  }

  try {
    // First validate and adjust the configuration to handle invalid combinations
    const validationResult = validateAndAdjustConfig({
      courseType: courseTypeKey,
      finishType,
      offsetEnabled,
      gates,
      windwardBeats
    });
    
    const validatedConfig = validationResult.adjustedConfig;
    
    // Use the validated/adjusted configuration for lookup
    const validatedGateConfig = {
      gate1: (validatedConfig.gates || []).includes('1') || (validatedConfig.gates || []).includes(1),
      gate3: (validatedConfig.gates || []).includes('3') || (validatedConfig.gates || []).includes(3), 
      gate4: (validatedConfig.gates || []).includes('4') || (validatedConfig.gates || []).includes(4),
      gate5: (validatedConfig.gates || []).includes('5') || (validatedConfig.gates || []).includes(5)
    };

    console.log('🔍 Original config:', { courseType: courseTypeKey, finishType, gates, offsetEnabled, windwardBeats });
    console.log('✅ Validation result:', validationResult);
    console.log('✅ Validated config:', validatedConfig);
    
    // Use the complete lookup system with validated configuration
    const completeSequence = generateSequenceFromCompleteLookup(
      validatedConfig.courseType, 
      validatedConfig.finishType, 
      validatedGateConfig, 
      validatedConfig.offsetEnabled, 
      validatedConfig.windwardBeats
    );
    
    if (completeSequence) {
      console.log('✅ Using complete lookup:', completeSequence);
      return completeSequence;
    }
    
    // If no sequence found, fail clearly with detailed error info
    const gateKey = Object.entries(validatedGateConfig)
      .filter(([_, active]) => active)
      .map(([gate, _]) => gate)
      .join('+');
    
    console.log('❌ Sequence lookup failed with validated config:', validatedConfig);
    console.log('❌ Validation adjustments made:', validationResult.adjustments);
    
    throw new Error(`No sequence found for: ${validatedConfig.courseType} / ${validatedConfig.finishType} / Gates: ${gateKey || 'none'} / Offset: ${validatedConfig.offsetEnabled} / Beats: ${validatedConfig.windwardBeats}`);
    
  } catch (error) {
    console.error('❌ Course sequence lookup failed:', error.message);
    throw error; // Re-throw to fail spectacularly instead of hiding the problem
  }
};



export const parseSequenceDescription = (sequenceString) => {
  if (!sequenceString) return 'No sequence defined';
  
  const parts = sequenceString.split('-');
  const descriptions = [];

  parts.forEach((part) => {
    if (part === 'S') {
      descriptions.push('Start');
    } else if (part === 'F') {
      descriptions.push('Finish');
    } else if (part.match(/^\d+A$/)) {
      const markNum = part.slice(0, -1);
      descriptions.push(`Mark ${markNum} Offset`);
    } else if (part.includes('G')) {
      const markNum = part.replace(/G[PS]?/g, '');
      if (part.includes('GP')) {
        descriptions.push(`Mark ${markNum} Gate (Port)`);
      } else if (part.includes('GS')) {
        descriptions.push(`Mark ${markNum} Gate (Starboard)`);
      } else {
        descriptions.push(`Mark ${markNum} Gate`);
      }
    } else if (part.match(/^\d+[PS]$/)) {
      const markNum = part.slice(0, -1);
      const side = part.slice(-1) === 'P' ? 'Port' : 'Starboard';
      descriptions.push(`Mark ${markNum} (${side})`);
    } else if (part.match(/^\d+$/)) {
      descriptions.push(`Mark ${part}`);
    } else {
      descriptions.push(part);
    }
  });

  return descriptions.join(' → ');
};

export const getMarkDetectionRadius = (courseData) => {
  if (!courseData || Object.keys(courseData).length === 0) return 50;

  const {
    averageBoatLength = 10,
    gateWidth = 6
  } = courseData;

  const gateRadius = (averageBoatLength * gateWidth) / 2;
  const singleMarkRadius = averageBoatLength * 2;

  return Math.max(Math.max(gateRadius, singleMarkRadius), 25);
};

// Export validation and adjustment functions
export {
  getAvailableFinishTypes,
  getAvailableGateCombinations, 
  validateAndAdjustConfig
};
